/**
 * MapEditorScene — in-browser tile-based map editor (Phase 6).
 *
 * Entry points:
 *   - URL flag:  ?editor=1  (detected in PreloadScene, starts this scene instead of BriefingScene)
 *   - Menu:      "Map Editor" button on BriefingScene (future)
 *
 * Workflow:
 *   1. Editor opens with a blank or WFC-generated map
 *   2. Player places special objects: squad spawn, enemy spawn, destroy target,
 *      enemy_building, explosive_barrel, ammo_collectable
 *   4. "Test Play" launches GameScene with the edited map; a "← EDITOR" button
 *      in GameScene returns here
 *   5. "Save" serialises to Tiled JSON and POSTs to the backend (requires auth)
 *
 * Enemy patrol workflow:
 *   - Place one or more enemies (▲ ENEMY SPAWN mode, click empty cell)
 *   - Click an existing enemy to select it (yellow ring)
 *   - While selected, click empty cells to append waypoints (numbered dots)
 *   - Click the selected enemy again to deselect
 *   - Click another enemy while one is selected to switch selection
 *   - Right-click an enemy to delete it (and all its waypoints)
 *   - Right-click a waypoint dot to remove just that waypoint
 *   - Right-click any other placed object to delete it
 *
 * Buttons are laid out in two rows so they all fit in the 800px canvas.
 */

import Phaser from 'phaser';
import { API_BASE } from '../api';
import { getTilesetConfig } from './PreloadScene';
import { GameScene } from './GameScene';
import { MissionLayout } from '../generation/MissionOverlay';
import { TerrainGenerator } from '../generation/TerrainGenerator';
import { LevelFactory } from '../generation/LevelFactory';
import { isTileWalkable } from '../generation/TilesetLoader';
import { CollisionGeometryBuilder } from '../physics/CollisionGeometryBuilder';
import { buildPolysFromGridMap } from 'navmesh';

const TILE_SIZE = 32;
const COLS = 20;
const ROWS = 15;

type ObjectMode =
  | 'none'
  | 'squad_spawn'
  | 'enemy_spawn'
  | 'destroy_target'
  | 'enemy_building'
  | 'explosive_barrel'
  | 'ammo_collectable';

interface Waypoint {
  col: number;
  row: number;
}

interface PlacedObject {
  type: ObjectMode;
  col: number;
  row: number;
  /** Ordered patrol waypoints — only used by enemy_spawn objects. */
  waypoints?: Waypoint[];
}

export class MapEditorScene extends Phaser.Scene {
  private tiles: number[][] = [];
  private tileGraphics!: Phaser.GameObjects.Graphics;
  private objectLayer!: Phaser.GameObjects.Graphics;
  private placedObjects: PlacedObject[] = [];

  private objectMode: ObjectMode = 'none';

  /** The enemy currently selected for waypoint editing, or null. */
  private selectedEnemy: PlacedObject | null = null;

  // Toolbar buttons (indices match button array order)
  private toolbarBtns: Phaser.GameObjects.Rectangle[] = [];

  constructor() {
    super({ key: 'MapEditorScene' });
  }

  create(): void {
    const { width, height } = this.scale;

    this.cameras.main.fadeIn(400, 0, 0, 0);

    // Background
    this.add.rectangle(0, 0, width, height, 0x0a0f18).setOrigin(0, 0);

    // Init blank map
    this.initBlankMap();

    // Grid
    this.tileGraphics = this.add.graphics().setDepth(1);
    this.objectLayer = this.add.graphics().setDepth(2);
    this.renderGrid();

    // Toolbar (two rows below the grid)
    this.buildToolbar();

    // Suppress the browser context menu so right-click can be used for deletion
    this.input.mouse!.disableContextMenu();

    // Grid click handler
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        this.handleRightClick(pointer);
      } else {
        this.handleGridClick(pointer);
      }
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.leftButtonDown()) this.handleGridClick(pointer);
    });

    // When the scene wakes (returning from test play), fade back in
    this.events.on('wake', () => {
      this.cameras.main.fadeIn(300, 0, 0, 0);
    });
  }

  // -------------------------------------------------------------------------
  // Map initialisation
  // -------------------------------------------------------------------------

  private initBlankMap(): void {
    this.tiles = [];
    for (let r = 0; r < ROWS; r++) {
      this.tiles.push(new Array(COLS).fill(1)); // tile index 1 = grass
    }
    this.placedObjects = [];
    this.selectedEnemy = null;
  }

  private generateViaWFC(): void {
    const config = getTilesetConfig();
    if (!config) {
      console.warn('MapEditorScene: tileset config not loaded');
      return;
    }
    const generator = new TerrainGenerator();
    generator.setTilesetConfig(config);
    // skipSpawnZones: the editor lets the user place entities manually, so the
    // hardcoded absolute spawn-zone constraints (tuned for 40×40) must not be
    // applied to the smaller editor grid — they would force the entire right half
    // to grass on a 20-column canvas.
    const result = generator.generate(COLS, ROWS, undefined, { skipSpawnZones: true });
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        this.tiles[r][c] = result.tiles[r][c];
      }
    }
    this.placedObjects = [];
    this.selectedEnemy = null;
    this.renderGrid();
    this.renderObjects();
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  private renderGrid(): void {
    this.tileGraphics.clear();

    const tileColors: Record<number, number> = {
      1:  0x4a7c59,  // grass
      2:  0x5c5040,  // road_h
      3:  0x5c5040,  // road_v
      4:  0x4a4030,  // road_cross
      5:  0x888888,  // wall
      6:  0x9a9a9a,  // wall_corner
      7:  0x8b6914,  // door
      8:  0x4477aa,  // window
      9:  0x228b22,  // tree
      10: 0x3a7a3a,  // bush
      11: 0x1e6b9e,  // water
      12: 0xc2a86e,  // sand
      13: 0x7a6a5a,  // rubble
      14: 0x9b7b3a,  // crate
      15: 0x3a2e1e,  // trench
      16: 0x00cc66,  // spawn
    };

    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const idx = this.tiles[r][c];
        const color = tileColors[idx] ?? 0x333333;
        this.tileGraphics.fillStyle(color, 1);
        this.tileGraphics.fillRect(c * TILE_SIZE, r * TILE_SIZE, TILE_SIZE - 1, TILE_SIZE - 1);
      }
    }

    // Grid lines
    this.tileGraphics.lineStyle(1, 0x1a1a1a, 0.4);
    for (let r = 0; r <= ROWS; r++) {
      this.tileGraphics.lineBetween(0, r * TILE_SIZE, COLS * TILE_SIZE, r * TILE_SIZE);
    }
    for (let c = 0; c <= COLS; c++) {
      this.tileGraphics.lineBetween(c * TILE_SIZE, 0, c * TILE_SIZE, ROWS * TILE_SIZE);
    }
  }

  private renderObjects(): void {
    this.objectLayer.clear();

    for (const obj of this.placedObjects) {
      const x = obj.col * TILE_SIZE;
      const y = obj.row * TILE_SIZE;
      const cx = x + TILE_SIZE / 2;
      const cy = y + TILE_SIZE / 2;

      if (obj.type === 'squad_spawn') {
        this.objectLayer.fillStyle(0x00ff88, 0.8);
        this.objectLayer.fillCircle(cx, cy, 8);

      } else if (obj.type === 'enemy_spawn') {
        this.objectLayer.fillStyle(0xff4444, 0.8);
        this.objectLayer.fillTriangle(
          cx, y + 4,
          x + TILE_SIZE - 4, y + TILE_SIZE - 4,
          x + 4, y + TILE_SIZE - 4
        );

        // Highlight ring when selected for waypoint editing
        if (obj === this.selectedEnemy) {
          this.objectLayer.lineStyle(2, 0xffcc00, 1);
          this.objectLayer.strokeCircle(cx, cy, 13);
        }

        // Draw patrol route as dashed line segments with numbered waypoints
        if (obj.waypoints && obj.waypoints.length > 0) {
          let prevX = cx;
          let prevY = cy;
          for (let i = 0; i < obj.waypoints.length; i++) {
            const wp = obj.waypoints[i];
            const wpX = wp.col * TILE_SIZE + TILE_SIZE / 2;
            const wpY = wp.row * TILE_SIZE + TILE_SIZE / 2;
            this.drawDashedLine(prevX, prevY, wpX, wpY, 0xff4444, 0.5, 4, 4);
            // Draw numbered dot
            this.objectLayer.fillStyle(0xff8888, 1);
            this.objectLayer.fillCircle(wpX, wpY, 5);
            prevX = wpX;
            prevY = wpY;
          }
        }

      } else if (obj.type === 'destroy_target') {
        this.objectLayer.lineStyle(3, 0xffcc00, 1);
        this.objectLayer.strokeRect(x + 4, y + 4, TILE_SIZE - 8, TILE_SIZE - 8);

      } else if (obj.type === 'enemy_building') {
        this.objectLayer.fillStyle(0xcc2222, 0.85);
        this.objectLayer.fillRect(x + 3, y + 3, TILE_SIZE - 6, TILE_SIZE - 6);
        this.objectLayer.lineStyle(2, 0xff8888, 1);
        this.objectLayer.lineBetween(x + 7, y + 7, x + TILE_SIZE - 7, y + TILE_SIZE - 7);
        this.objectLayer.lineBetween(x + TILE_SIZE - 7, y + 7, x + 7, y + TILE_SIZE - 7);

      } else if (obj.type === 'explosive_barrel') {
        this.objectLayer.fillStyle(0xff8800, 0.9);
        this.objectLayer.fillCircle(cx, cy, 9);
        this.objectLayer.lineStyle(2, 0xffcc44, 1);
        this.objectLayer.strokeCircle(cx, cy, 9);

      } else if (obj.type === 'ammo_collectable') {
        const half = TILE_SIZE / 2 - 2;
        this.objectLayer.fillStyle(0xffee00, 0.9);
        this.objectLayer.fillTriangle(cx, cy - half, cx + half, cy, cx - half, cy);
        this.objectLayer.fillTriangle(cx - half, cy, cx + half, cy, cx, cy + half);
      }
    }
  }

  /** Draw a dashed line between two points. */
  private drawDashedLine(
    x1: number, y1: number,
    x2: number, y2: number,
    color: number,
    alpha: number,
    dashLen: number,
    gapLen: number
  ): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const total = Math.sqrt(dx * dx + dy * dy);
    if (total === 0) return;
    const ux = dx / total;
    const uy = dy / total;
    const step = dashLen + gapLen;
    this.objectLayer.lineStyle(2, color, alpha);
    let dist = 0;
    while (dist < total) {
      const segEnd = Math.min(dist + dashLen, total);
      this.objectLayer.lineBetween(
        x1 + ux * dist, y1 + uy * dist,
        x1 + ux * segEnd, y1 + uy * segEnd
      );
      dist += step;
    }
  }

  // -------------------------------------------------------------------------
  // Input
  // -------------------------------------------------------------------------

  private handleGridClick(pointer: Phaser.Input.Pointer): void {
    const col = Math.floor(pointer.x / TILE_SIZE);
    const row = Math.floor(pointer.y / TILE_SIZE);
    if (col < 0 || col >= COLS || row < 0 || row >= ROWS) return;

    if (this.objectMode === 'squad_spawn') {
      // Toggle: click existing soldier to remove it
      const idx = this.placedObjects.findIndex(
        (o) => o.type === 'squad_spawn' && o.col === col && o.row === row
      );
      if (idx !== -1) {
        this.placedObjects.splice(idx, 1);
      } else {
        this.placedObjects = this.placedObjects.filter((o) => !(o.col === col && o.row === row));
        this.placedObjects.push({ type: 'squad_spawn', col, row });
      }
      this.renderObjects();
      return;
    }

    if (this.objectMode === 'enemy_spawn') {
      const clickedEnemy = this.placedObjects.find(
        (o) => o.type === 'enemy_spawn' && o.col === col && o.row === row
      );

      if (clickedEnemy) {
        // Toggle selection: click selected enemy = deselect; click another = switch
        this.selectedEnemy = clickedEnemy === this.selectedEnemy ? null : clickedEnemy;
        this.renderObjects();
        return;
      }

      // Clicked an empty (or non-enemy) cell
      if (this.selectedEnemy) {
        // Append waypoint to the selected enemy's patrol route
        if (!this.selectedEnemy.waypoints) this.selectedEnemy.waypoints = [];
        this.selectedEnemy.waypoints.push({ col, row });
        this.renderObjects();
        return;
      }

      // No enemy selected — place a new enemy (remove any non-enemy object at cell first)
      this.placedObjects = this.placedObjects.filter((o) => !(o.col === col && o.row === row));
      this.placedObjects.push({ type: 'enemy_spawn', col, row, waypoints: [] });
      this.renderObjects();
      return;
    }

    // All other modes: place object, replacing whatever is already at the cell
    this.placedObjects = this.placedObjects.filter((o) => !(o.col === col && o.row === row));
    this.placedObjects.push({ type: this.objectMode, col, row });
    this.renderObjects();
  }

  /**
   * Right-click handler: delete the enemy, waypoint, or object at the clicked cell.
   * Priority: enemy body → waypoint dot → any other object.
   */
  private handleRightClick(pointer: Phaser.Input.Pointer): void {
    const col = Math.floor(pointer.x / TILE_SIZE);
    const row = Math.floor(pointer.y / TILE_SIZE);
    if (col < 0 || col >= COLS || row < 0 || row >= ROWS) return;

    // Delete an enemy (and its waypoints) if clicked on its cell
    const enemyIdx = this.placedObjects.findIndex(
      (o) => o.type === 'enemy_spawn' && o.col === col && o.row === row
    );
    if (enemyIdx !== -1) {
      if (this.selectedEnemy === this.placedObjects[enemyIdx]) {
        this.selectedEnemy = null;
      }
      this.placedObjects.splice(enemyIdx, 1);
      this.renderObjects();
      return;
    }

    // Delete a waypoint if clicked on a waypoint cell belonging to any enemy
    for (const obj of this.placedObjects) {
      if (obj.type === 'enemy_spawn' && obj.waypoints) {
        const wpIdx = obj.waypoints.findIndex((wp) => wp.col === col && wp.row === row);
        if (wpIdx !== -1) {
          obj.waypoints.splice(wpIdx, 1);
          this.renderObjects();
          return;
        }
      }
    }

    // Delete any other placed object at this cell
    const objIdx = this.placedObjects.findIndex((o) => o.col === col && o.row === row);
    if (objIdx !== -1) {
      this.placedObjects.splice(objIdx, 1);
      this.renderObjects();
    }
  }

  // -------------------------------------------------------------------------
  // Toolbar — two rows of buttons to fit within the 800px canvas
  // -------------------------------------------------------------------------

  private buildToolbar(): void {
    // Row 1 starts just below the grid; row 2 follows immediately after.
    const row1Y = ROWS * TILE_SIZE + 8;
    const row2Y = row1Y + 40;

    // 5 buttons per row, spanning the full grid width (640px) plus a little extra.
    // Available width = 790px (10px left margin, 0px right). 5 buttons + 4 gaps of 6px.
    const COLS_PER_ROW = 5;
    const TOTAL_W = 790;
    const GAP = 6;
    const btnW = Math.floor((TOTAL_W - (COLS_PER_ROW - 1) * GAP) / COLS_PER_ROW); // 151px

    const buttons: { label: string; color: number; action: () => void; modeIndex?: number }[] = [
      { label: '⚙ GENERATE',      color: 0x00ff88, action: () => this.generateViaWFC() },
      { label: '● SQUAD SPAWN',   color: 0x00ff88, action: () => this.setMode('squad_spawn', 1),    modeIndex: 1 },
      { label: '▲ ENEMY SPAWN',   color: 0xff4444, action: () => this.setMode('enemy_spawn', 2),    modeIndex: 2 },
      { label: '□ DESTROY TARGET',color: 0xffcc00, action: () => this.setMode('destroy_target', 3), modeIndex: 3 },
      { label: '■ ENEMY BUILDING',color: 0xff6666, action: () => this.setMode('enemy_building', 4), modeIndex: 4 },
      { label: '◉ EXP BARREL',    color: 0xff8800, action: () => this.setMode('explosive_barrel', 5), modeIndex: 5 },
      { label: '◆ AMMO',          color: 0xffee00, action: () => this.setMode('ammo_collectable', 6), modeIndex: 6 },
      { label: '▶ TEST PLAY',     color: 0xffffff, action: () => this.testPlay() },
      { label: '💾 SAVE',         color: 0x4499ff, action: () => this.saveMap() },
    ];

    const isLoggedIn = this.checkAuth();
    const saveIdx = buttons.length - 1;

    buttons.forEach((btn, i) => {
      const rowIdx = Math.floor(i / COLS_PER_ROW);
      const colIdx = i % COLS_PER_ROW;
      const bx = 10 + colIdx * (btnW + GAP);
      const by = rowIdx === 0 ? row1Y : row2Y;
      const disabled = i === saveIdx && !isLoggedIn;
      const strokeColor = disabled ? 0x334455 : btn.color;

      const bg = this.add
        .rectangle(bx + btnW / 2, by + 16, btnW, 32, 0x111111)
        .setStrokeStyle(1, strokeColor)
        .setDepth(5);

      if (!disabled) {
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', btn.action);
      }

      this.add
        .text(bx + btnW / 2, by + 16, btn.label, {
          fontSize: '10px',
          color: disabled ? '#334455' : `#${btn.color.toString(16).padStart(6, '0')}`,
          fontFamily: 'monospace',
        })
        .setOrigin(0.5)
        .setDepth(6);

      this.toolbarBtns.push(bg);

    });
  }

  private setMode(mode: ObjectMode, toolbarIndex: number): void {
    this.objectMode = mode;
    this.selectedEnemy = null;
    this.renderObjects();
    this.updateToolbarHighlight(toolbarIndex);
  }

  private updateToolbarHighlight(activeIndex: number | null): void {
    this.toolbarBtns.forEach((btn, i) => {
      btn.setFillStyle(activeIndex !== null && i === activeIndex ? 0x223322 : 0x111111);
    });
  }

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  /** Returns true if a valid (non-expired) JWT is present in sessionStorage. */
  private checkAuth(): boolean {
    const token = sessionStorage.getItem('ff_auth_token');
    if (!token) return false;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1]));
      return !!payload.exp && payload.exp > Date.now() / 1000;
    } catch {
      return false;
    }
  }

  private testPlay(): void {
    const json = this.buildTiledJSON();

    // GameScene looks for the key 'level_generated' — use that exact key.
    this.game.cache.tilemap.remove('level_generated');
    this.game.cache.tilemap.add('level_generated', {
      data: json,
      format: Phaser.Tilemaps.Formats.TILED_JSON,
    });

    // Build a MissionLayout from editor-placed objects so GameScene uses those
    // positions instead of falling back to hardcoded SQUAD_SPAWNS / ENEMY_SPAWNS.
    const editorSquadSpawns = this.placedObjects
      .filter((o) => o.type === 'squad_spawn')
      .map((o) => ({ x: o.col, y: o.row }));

    const editorEnemySpawns = this.placedObjects
      .filter((o) => o.type === 'enemy_spawn')
      .map((o) => ({
        x: o.col,
        y: o.row,
        type: 'RIFLEMAN',
        waypoints: (o.waypoints ?? []).map((wp) => ({ x: wp.col, y: wp.row })),
      }));

    const destroyTargetObj = this.placedObjects.find((o) => o.type === 'destroy_target');

    const editorBarrels = this.placedObjects
      .filter((o) => o.type === 'explosive_barrel')
      .map((o) => ({ x: o.col, y: o.row }));

    // Ammo collectables default to grenades (×3) — the editor has no type selector
    const editorAmmoPickups = this.placedObjects
      .filter((o) => o.type === 'ammo_collectable')
      .map((o) => ({ x: o.col, y: o.row, type: 'grenade', amount: 3 }));

    const editorEnemyBuildings = this.placedObjects
      .filter((o) => o.type === 'enemy_building')
      .map((o) => ({ x: o.col, y: o.row }));

    const missionLayout: MissionLayout = {
      // Guarantee at least one squad spawn so GameScene never crashes
      playerSpawn: editorSquadSpawns.length > 0 ? editorSquadSpawns : [{ x: 3, y: 3 }],
      enemySpawns: editorEnemySpawns,
      objectives: [{ type: 'kill_all' }],
      barrels: editorBarrels,
      ammoPickups: editorAmmoPickups,
      enemyBuildings: editorEnemyBuildings,
      ...(destroyTargetObj ? { destroyTarget: { x: destroyTargetObj.col, y: destroyTargetObj.row } } : {}),
    };

    // Set up GameScene statics so it loads the editor map instead of a static level.
    GameScene.useGeneratedLevel = true;
    GameScene.generatedMap = { width: COLS, height: ROWS, tiles: this.tiles };
    GameScene.missionLayout = missionLayout;

    // Signal that GameScene was launched from the editor so it shows "← EDITOR".
    sessionStorage.setItem('ff_editor_test_mode', '1');

    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      // Use switch() to sleep this scene (preserving state) instead of stop()
      this.scene.switch('GameScene');
    });
  }

  private buildCollisionGeometry(): {
    solidPolygons: { x: number; y: number }[][];
    walkablePolygons: { x: number; y: number }[][];
  } {
    // Build walkability grid: 0 = open, 1 = blocked
    const tilesetConfig = getTilesetConfig();
    const walkabilityGrid: number[][] = [];
    for (let r = 0; r < ROWS; r++) {
      walkabilityGrid.push([]);
      for (let c = 0; c < COLS; c++) {
        const tileIndex = this.tiles[r][c];
        const walkable = tilesetConfig ? isTileWalkable(tilesetConfig, tileIndex) : tileIndex === 1;
        walkabilityGrid[r].push(walkable ? 0 : 1);
      }
    }

    // Tile-scale collision geometry (fast path, < 50 ms)
    const builder = new CollisionGeometryBuilder({ tileSize: TILE_SIZE });
    const geom = builder.build([], walkabilityGrid, null);

    // Scale solid polygon coords from tile-scale bitmap coords to world pixels
    const solidPolygons = geom.solidPolygons.map((poly) =>
      poly.map((p) => ({ x: p.x * TILE_SIZE, y: p.y * TILE_SIZE }))
    );

    // Walkable polygons (open-space hulls) in world pixels
    const walkablePolygons = (buildPolysFromGridMap(
      walkabilityGrid,
      TILE_SIZE,
      TILE_SIZE,
      (v: number) => v === 0,
    ) as { x: number; y: number }[][]);

    return { solidPolygons, walkablePolygons };
  }

  private saveMap(): void {
    const token = sessionStorage.getItem('ff_auth_token');
    if (!this.checkAuth() || !token) {
      window.location.href = '/pages/auth.html#login';
      return;
    }

    const title = window.prompt('Map title:', 'My Custom Map') ?? 'My Custom Map';
    const author = window.prompt('Author name:', 'Anonymous') ?? 'Anonymous';

    const collisionGeometry = this.buildCollisionGeometry();

    fetch(`${API_BASE}/v1/levels`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        title,
        author,
        difficulty: 'normal',
        tileData: JSON.stringify(this.tiles),
        collisionGeometry,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((data: any) => {
        const id: string = data.levelId ?? data.id ?? '';
        const url = `/?level=${encodeURIComponent(id)}`;
        window.history.pushState({}, '', url);
        this.showToast(`Saved! Share: ${window.location.origin}${url}`);
        this.captureAndUploadScreenshot(id);
      })
      .catch((err) => {
        console.error('[MapEditorScene] Save failed:', err);
        this.showToast('Save failed — check console for details');
      });
  }

  private buildTiledJSON(): object {
    const factory = new LevelFactory();
    const generatedMap = { width: COLS, height: ROWS, tiles: this.tiles };
    const json = factory.buildTiledJSON(generatedMap) as any;

    json.metadata = {
      name: 'Custom Map',
      difficulty: 'normal',
      objectives: [{ label: 'Eliminate all enemies' }],
      startingPositions: this.placedObjects
        .filter((o) => o.type === 'squad_spawn')
        .map((o) => ({ x: o.col, y: o.row })),
      enemySpawns: this.placedObjects
        .filter((o) => o.type === 'enemy_spawn')
        .map((o, idx) => ({
          x: o.col,
          y: o.row,
          type: 'RIFLEMAN',
          id: idx + 1,
          patrol: (o.waypoints ?? []).map((wp) => ({ x: wp.col, y: wp.row })),
        })),
      destroyTarget: (() => {
        const t = this.placedObjects.find((o) => o.type === 'destroy_target');
        return t ? { x: t.col, y: t.row } : null;
      })(),
      enemyBuildings: this.placedObjects
        .filter((o) => o.type === 'enemy_building')
        .map((o, idx) => ({ x: o.col, y: o.row, id: idx + 1 })),
      explosiveBarrels: this.placedObjects
        .filter((o) => o.type === 'explosive_barrel')
        .map((o, idx) => ({ x: o.col, y: o.row, id: idx + 1 })),
      ammoCollectables: this.placedObjects
        .filter((o) => o.type === 'ammo_collectable')
        .map((o, idx) => ({ x: o.col, y: o.row, id: idx + 1 })),
    };
    return json;
  }

  private captureAndUploadScreenshot(levelId: string): void {
    this.game.renderer.snapshot(async (image: HTMLImageElement | Phaser.Display.Color) => {
      if (!(image instanceof HTMLImageElement)) return;
      try {
        const urlRes = await fetch(`${API_BASE}/v1/levels/${encodeURIComponent(levelId)}/screenshot-url`);
        if (!urlRes.ok) return;
        const { uploadUrl, screenshotUrl } = await urlRes.json();
        const resp = await fetch(image.src);
        const blob = await resp.blob();
        const put = await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': 'image/png' } });
        if (!put.ok) return;
        await fetch(`${API_BASE}/v1/levels/${encodeURIComponent(levelId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ screenshotUrl }),
        });
      } catch { /* silent */ }
    });
  }

  private showToast(message: string): void {
    const { width, height } = this.scale;
    const toast = this.add
      .text(width / 2, height - 40, message, {
        fontSize: '12px',
        color: '#ffcc00',
        fontFamily: 'monospace',
        backgroundColor: '#111111',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(20);

    this.time.delayedCall(2500, () => toast.destroy());
  }
}
